/**
 * Enums synchronized with backend Django models and serializers
 * Source: /hoxtonmix-api
 *
 * IMPORTANT: These enums MUST stay in sync with the backend.
 * Any changes to backend choices/enums should be reflected here.
 */

// ============================================================================
// Mail Related Enums
// ============================================================================

/**
 * Mail status enum
 * Backend: apps/mailbox/enums.py - MailStatus
 */
export enum MailStatus {
  AWAITING_CUSTOMER_ACTION = 'AWAITING_CUSTOMER_ACTION',
  AWAITING_FORWARD_RELEASED = 'AWAITING_FORWARD_RELEASED',
  AWAITING_SCAN = 'AWAITING_SCAN',
  AWAITING_DESTRUCTION = 'AWAITING_DESTRUCTION',
  COLLECTED = 'COLLECTED',
  CREATED = 'CREATED',
  DESTROYED = 'DESTROYED',
  FORWARDED = 'FORWARDED',
  FORWARDED_ON_HOLD_NO_ID = 'FORWARDED_ON_HOLD_NO_ID',
  ON_HOLD_INVOICE = 'ON_HOLD_INVOICE',
  ON_HOLD_KYC_ID = 'ON_HOLD_KYC_ID',
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  REFUSED = 'REFUSED',
  REJECTED = 'REJECTED',
  REQUESTED_FORWARD = 'REQUESTED_FORWARD',
  REQUESTED_SCAN = 'REQUESTED_SCAN',
  RETURNED = 'RETURNED',
  SCANNED = 'SCANNED',
  SCANNED_ON_HOLD_NO_ID = 'SCANNED_ON_HOLD_NO_ID',
  SPAM = 'SPAM',
  ACCOUNT_FROZEN = 'ACCOUNT_FROZEN',
  AWAITING_FORWARD = 'AWAITING_FORWARD',
  AWAITING_MACHINE_SCAN = 'AWAITING_MACHINE_SCAN',
}

/**
 * Mail state enum
 * Backend: apps/mailbox/enums.py
 */
export enum MailState {
  TEST = 'TEST',
  LIVE = 'LIVE',
}

/**
 * Mailbox action types
 * Backend: apps/mailbox/enums.py - MailboxAction
 */
export enum MailActionType {
  DOWNLOAD = 'DOWNLOAD',
  FORWARD = 'FORWARD',
  SCAN = 'SCAN',
  DESTROY = 'DESTROY',
  MARK_AS_READ = 'MARK_AS_READ',
  MARK_AS_UNREAD = 'MARK_AS_UNREAD',
}

/**
 * Mail envelope type enum
 * Backend: apps/mailbox/enums.py - MailEnvelopeType
 */
export enum MailEnvelopeType {
  C4 = 'C4',
  C5 = 'C5',
  C6 = 'C6',
  DL = 'DL',
  PARCEL = 'PARCEL',
  NON_STANDARD = 'NON_STANDARD',
}

// ============================================================================
// Notification Related Enums
// ============================================================================

/**
 * Message type enum
 * Backend: apps/notifications/enums.py - MessageType
 */
export enum MessageType {
  EMAIL = 'EMAIL',
  PUSH = 'PUSH',
  SMS = 'SMS',
}

/**
 * Message delivery status enum
 * Backend: apps/notifications/enums.py - MessageDeliveryStatus
 */
export enum MessageDeliveryStatus {
  CREATED = 'CREATED',
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  READ = 'READ',
  FAILEDBOUNCEDHARD = 'FAILEDBOUNCEDHARD',
  FAILEDBOUNCEDSOFT = 'FAILEDBOUNCEDSOFT',
  FAILEDOTHER = 'FAILEDOTHER',
}

// ============================================================================
// KYC Related Enums
// ============================================================================

/**
 * KYC status type
 * Backend: apps/kyc/enums.py
 */
export enum KYCStatusType {
  NOT_STARTED = 'NOT_STARTED',
  STARTED = 'STARTED',
  EXPIRED = 'EXPIRED',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
  CREATED = 'CREATED',
  RESUBMISSION = 'RESUBMISSION',
  REVIEW = 'REVIEW',
}

/**
 * KYC link type
 * Backend: apps/kyc/serializers.py
 */
export enum KYCLinkType {
  ID = 'ID',
  ADDRESS = 'ADDRESS',
}

/**
 * Company ID status
 * Backend: apps/customers/enums.py
 */
export enum CompanyIDStatus {
  NotChecked = 'NotChecked',
  EmailSent = 'EmailSent',
  EmailClicked = 'EmailClicked',
  IdSubmitted = 'IdSubmitted',
  IdCheckedAwaitingSecondOpinion = 'IdCheckedAwaitingSecondOpinion',
  IdCheckedConfirmed = 'IdCheckedConfirmed',
}

/**
 * Companies House status
 * Backend: apps/customers/enums.py
 */
export enum CompaniesHouseStatus {
  NotChecked = 'NotChecked',
  CheckedFound = 'CheckedFound',
  CheckedNotFound = 'CheckedNotFound',
  CheckedFoundInternational = 'CheckedFoundInternational',
}

/**
 * KYC check status
 * Backend: apps/identifications/enums.py
 */
export enum KYCCheckStatus {
  NotChecked = 'NotChecked',
  CheckedPassed = 'CheckedPassed',
  CheckedFailed = 'CheckedFailed',
}

/**
 * AML status type
 * Backend: apps/kyc/enums.py
 */
export enum AMLStatusType {
  NO_MATCH = 'NO_MATCH',
  POSSIBLE_MATCH = 'POSSIBLE_MATCH',
}

/**
 * AML check status
 * Backend: apps/identifications/enums.py
 */
export enum AMLCheckStatus {
  NOT_CHECKED = 'NOT_CHECKED',
  CHECKED_PASSED = 'CHECKED_PASSED',
  CHECKED_FAILED = 'CHECKED_FAILED',
}

/**
 * Verification media status
 * Backend: apps/kyc/enums.py
 */
export enum VerificationMediaStatus {
  CREATED = 'CREATED',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
}

// ============================================================================
// Phone Related Enums
// ============================================================================

/**
 * Phone type enum
 * Backend: apps/customers/enums.py - PhoneType
 */
export enum PhoneType {
  WHATSAPPFORBUSINESS = 'WHATSAPPFORBUSINESS',
  VOICE = 'VOICE',
}

/**
 * UK phone number type (mobile vs landline)
 * Backend: apps/customers/serializers.py (AreaCodeSerializer)
 */
export enum UKPhoneNumberType {
  UK_MOBILE = 'UK_MOBILE',
  UK_LANDLINE = 'UK_LANDLINE',
}

// ============================================================================
// Payment Related Enums
// ============================================================================

/**
 * Payment source status
 * Backend: Chargebee integration
 */
export enum PaymentSourceStatus {
  VALID = 'valid',
  EXPIRING = 'expiring',
  EXPIRED = 'expired',
  INVALID = 'invalid',
  PENDING_VERIFICATION = 'pending_verification',
}

/**
 * Payment source type
 * Backend: Chargebee integration
 */
export enum PaymentSourceType {
  CARD = 'card',
  PAYPAL_EXPRESS_CHECKOUT = 'paypal_express_checkout',
  AMAZON_PAYMENTS = 'amazon_payments',
  DIRECT_DEBIT = 'direct_debit',
}

// ============================================================================
// Invoice Related Enums
// ============================================================================

/**
 * Line item entity type
 * Backend: Chargebee integration
 */
export enum LineItemType {
  PLAN_ITEM_PRICE = 'plan_item_price',
  ADHOC = 'adhoc',
  ADDON_ITEM_PRICE = 'addon_item_price',
  CHARGE_ITEM_PRICE = 'charge_item_price',
}

// ============================================================================
// Offer Related Enums
// ============================================================================

/**
 * Tag type enum
 * Backend: apps/tags/enums.py - TagType
 */
export enum TagType {
  OFFER = 'OFFER',
}

// ============================================================================
// Subscription Related Enums
// ============================================================================

/**
 * Billing period enum
 * Backend: apps/subscriptions/enums.py
 */
export enum BillingPeriod {
  MONTHLY = 1,
  ANNUAL = 2,
}

// ============================================================================
// Permission Related Enums
// ============================================================================

/**
 * Permission codes
 * Backend: apps/customers/enums.py - PermissionCode
 */
export enum PermissionCode {
  ADMIN = 'ADMIN', // Admin permission
  OWNER = 'OWNER', // Doesn't exist in backend but frontend checks for owner status
  MANAGE_ACCOUNT = 'MANAGE_ACCOUNT', // Access the mail link
  READ_MAIL = 'READ_MAIL', // Access the mail link
  ACTION_MAIL = 'ACTION_MAIL', // Dictates whether paid actions come back for mail (Scan / Forward)
  TOPUP = 'TOPUP', // Add funds to account
  MANAGE_BILLING = 'MANAGE_BILLING', // Access Chargebee
  READ_INVOICE = 'READ_INVOICE', // Access account and billing link
  READ_MEMBER = 'READ_MEMBER', // Access teams link
  INVITE_MEMBER = 'INVITE_MEMBER', // Invite members button
  DELETE_MEMBER = 'DELETE_MEMBER', // Delete members button
  EDIT_MEMBER = 'EDIT_MEMBER', // Edit members button
}

// ============================================================================
// Mail Category Related Enums
// ============================================================================

/**
 * Mail category names
 * Backend: apps/mailbox/enums.py or AI categorization
 */
export enum CategoryName {
  FINANCIAL = 'Financial',
  UTILITIES_SERVICES = 'Utilities & Services',
  TAXATION = 'Taxation',
  GOVERNMENT_PUBLIC_SECTOR = 'Government & Public Sector',
  HUMAN_RESOURCES = 'Human Resources',
  OFFICIAL_LEGAL = 'Official / Legal',
  SUPPLIERS_VENDORS = 'Suppliers & Vendors',
  MARKETING_ADVERTISING = 'Marketing & Advertising',
  TECHNOLOGY_IT = 'Technology & IT',
  INDUSTRY_UPDATES = 'Industry Updates',
  CLIENTS_CUSTOMERS = 'Clients & Customers',
}

// ============================================================================
// Plan Related Enums
// ============================================================================

/**
 * Plan slug enum
 * Backend: apps/subscriptions/enums.py
 */
export enum PlanSlug {
  WHATSAPP = 'virtual-office-whatsapp-for-business-number',
}

// ============================================================================
// Device Related Enums
// ============================================================================

/**
 * Device platform enum
 * Backend: apps/devices/enums.py
 */
export enum DevicePlatform {
  ANDROID = 'android',
  IOS = 'ios',
}

export enum BusinessType {
  'LIMITED_COMPANY' = 'LIMITED_COMPANY',
  'SOLE_TRADER' = 'SOLE_TRADER',
  'NON_PROFIT' = 'CHARITY',
  'UNINCORPORATED' = 'UNINCORPORATED',
  'TRUST' = 'TRUST',
  'ASSOCIATION' = 'ASSOCIATION',
}
